import { ForwardedRef, forwardRef, useCallback, useMemo } from 'react';
import { Calendar } from '@components/calendar';
import { IconCross } from '@components/icons';
import { SupportedLanguages } from '@constants/constants.type';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { getFnsLocale } from '@hooks/use-get-locale/use-get-locale';
import classNames from 'classnames';
import moment from 'moment-timezone';

import { type DatePickerProps } from './date-picker.type';

// Convert a moment to a browser-local Date preserving the displayed day.
// DayPicker uses browser-local dates internally, so we extract year/month/day
// from the moment's timezone display to ensure the correct day is highlighted.
const momentToDate = (m: moment.Moment | null | undefined): Date | undefined => {
  if (!m) return undefined;
  return new Date(m.year(), m.month(), m.date());
};

export const DatePicker = forwardRef(
  (
    {
      value,
      onChange,
      placeholder,
      minBookingDate,
      maxBookingDate,
      initialVisibleMonth,
      hasError,
      locale,
      showClearDate,
      disabled,
      readOnly,
      required,
      id,
      name,
      timezone,
      'data-testid': dataTestId,
    }: DatePickerProps,
    _: ForwardedRef<HTMLInputElement>
  ) => {
    const dateFnsLocale = useMemo(() => getFnsLocale((locale ?? 'en') as SupportedLanguages), [locale]);

    const minDate = useMemo(() => momentToDate(minBookingDate), [minBookingDate]);
    const maxDate = useMemo(() => momentToDate(maxBookingDate), [maxBookingDate]);
    const selectedDate = useMemo(() => momentToDate(value), [value]);

    const defaultMonth = useMemo(() => {
      if (initialVisibleMonth) return momentToDate(initialVisibleMonth());
      if (minBookingDate) return momentToDate(minBookingDate);
      return undefined;
    }, [initialVisibleMonth, minBookingDate]);

    const displayValue = useMemo(() => {
      if (!value) return '';
      const format = moment.localeData(locale ?? 'es').longDateFormat('LL');
      return value.format(format);
    }, [value, locale]);

    const handleSelect = useCallback(
      (date: Date | undefined) => {
        if (!date) {
          onChange?.(null);
          return;
        }
        // Extract the visual day the user clicked (year/month/day from the browser-local Date).
        // Create the moment at midnight in the target timezone so the date is always
        // interpreted in the venue/event timezone, not the browser timezone.
        const tz = timezone ?? value?.tz?.();
        if (tz) {
          onChange?.(moment.tz({ year: date.getFullYear(), month: date.getMonth(), date: date.getDate() }, tz));
        } else {
          onChange?.(moment(date));
        }
      },
      [onChange, value, timezone]
    );

    const handleClear = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange?.(null);
      },
      [onChange]
    );

    const isInteractive = !disabled && !readOnly;

    return (
      <Popover>
        <PopoverButton
          as='div'
          className={classNames('flex text-body-m relative rounded-md border cursor-pointer', 'focus:text-subtitle-m', {
            'border-critical-no-bg focus:border-critical-no-bg hover:border-critical-no-bg': hasError,
            'border-soft focus:border-hard hover:border-hard': !hasError && isInteractive,
            'text-primary': isInteractive,
            'cursor-not-allowed border-soft text-tertiary': !isInteractive,
          })}
          disabled={!isInteractive}
          data-testid={dataTestId}
          role='button'
          tabIndex={isInteractive ? 0 : -1}>
          <input
            id={id}
            name={name}
            type='text'
            readOnly
            required={required}
            disabled={disabled}
            value={displayValue}
            placeholder={placeholder}
            className={classNames(
              'w-full bg-transparent border-none outline-none p-3 pl-2 text-body-m',
              'focus:text-subtitle-m cursor-pointer',
              {
                'text-primary': isInteractive,
                'text-tertiary cursor-not-allowed pointer-events-none': !isInteractive,
              }
            )}
            tabIndex={-1}
          />
          {showClearDate && value && isInteractive && (
            <button
              type='button'
              onClick={handleClear}
              className='absolute right-2 top-1/2 -translate-y-1/2 p-1'
              aria-label='Clear date'>
              <IconCross className='w-3 h-3 text-primary' />
            </button>
          )}
        </PopoverButton>

        {isInteractive && (
          <PopoverPanel
            anchor='bottom start'
            className='z-50 rounded-lg shadow-calendar bg-surface-primary mt-1 scrollbar-xceed'>
            {({ close }) => (
              <Calendar
                mode='single'
                selected={selectedDate}
                onSelect={(date) => {
                  handleSelect(date);
                  close();
                }}
                locale={dateFnsLocale}
                weekStartsOn={dateFnsLocale?.options?.weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6}
                minDate={minDate}
                maxDate={maxDate}
                defaultMonth={defaultMonth}
              />
            )}
          </PopoverPanel>
        )}
      </Popover>
    );
  }
);
